import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [permission, setPermission] = useState('default');
    const [swRegistration, setSwRegistration] = useState(null);
    // Use a ref (not state) for the map of scheduled timeouts.
    // It does NOT need to trigger re-renders, and storing it as state caused
    // an infinite render loop: each setState changed the identity of the
    // useCallback-memoised functions, which in turn re-fired the calendar's
    // useEffect that called scheduleEventReminders -> setState -> loop (React #185).
    const scheduledRemindersRef = useRef(new Map());
    
    // Register Service Worker
    useEffect(() => {
        if ('serviceWorker' in navigator && 'Notification' in window) {
            navigator.serviceWorker.register('/sw.js')
                .then((registration) => {
                    console.log('Service Worker registered:', registration);
                    setSwRegistration(registration);
                })
                .catch((error) => {
                    console.error('Service Worker registration failed:', error);
                });
            
            // Check current permission
            setPermission(Notification.permission);
        }
    }, []);
    
    // Request notification permission
    const requestPermission = useCallback(async () => {
        if (!('Notification' in window)) {
            console.log('Notifications not supported');
            return false;
        }
        
        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            return result === 'granted';
        } catch (error) {
            console.error('Permission request failed:', error);
            return false;
        }
    }, []);
    
    // Schedule a notification
    const scheduleNotification = useCallback((eventId, title, body, triggerTime) => {
        if (permission !== 'granted') {
            console.log('Notification permission not granted');
            return null;
        }
        
        const now = Date.now();
        const delay = triggerTime - now;
        
        if (delay <= 0) {
            console.log('Trigger time is in the past');
            return null;
        }
        
        // Cancel existing reminder for this event
        if (scheduledRemindersRef.current.has(eventId)) {
            clearTimeout(scheduledRemindersRef.current.get(eventId));
        }
        
        // Schedule new reminder
        const timeoutId = setTimeout(() => {
            // Use Service Worker if available, otherwise fallback to Notification API
            if (swRegistration && swRegistration.active) {
                swRegistration.active.postMessage({
                    type: 'SCHEDULE_NOTIFICATION',
                    title,
                    body,
                    delay: 0,
                    eventId
                });
            } else if ('Notification' in window && permission === 'granted') {
                new Notification(title, {
                    body,
                    icon: '/logo192.png',
                    badge: '/logo192.png',
                    tag: eventId,
                    vibrate: [100, 50, 100]
                });
            }
            
            // Remove from scheduled
            scheduledRemindersRef.current.delete(eventId);
        }, delay);
        
        scheduledRemindersRef.current.set(eventId, timeoutId);
        
        return timeoutId;
    }, [permission, swRegistration]);
    
    // Cancel a scheduled notification
    const cancelNotification = useCallback((eventId) => {
        if (scheduledRemindersRef.current.has(eventId)) {
            clearTimeout(scheduledRemindersRef.current.get(eventId));
            scheduledRemindersRef.current.delete(eventId);
        }
    }, []);
    
    // Schedule reminders for events
    const scheduleEventReminders = useCallback((events) => {
        if (permission !== 'granted') return;
        
        const now = Date.now();
        
        events.forEach(event => {
            if (!event.reminder || event.reminder === 'none') return;
            
            const eventStart = new Date(event.start).getTime();
            const reminderMinutes = parseInt(event.reminder, 10);
            
            if (isNaN(reminderMinutes) || reminderMinutes <= 0) return;
            
            const triggerTime = eventStart - (reminderMinutes * 60 * 1000);
            
            // Only schedule future reminders
            if (triggerTime > now) {
                const title = `Terminerinnerung: ${event.title}`;
                const minuteText = reminderMinutes === 1440 ? '1 Tag' : 
                                   reminderMinutes === 60 ? '1 Stunde' : 
                                   `${reminderMinutes} Minuten`;
                const body = `Dein Termin beginnt in ${minuteText}`;
                
                scheduleNotification(event.id, title, body, triggerTime);
            }
        });
    }, [permission, scheduleNotification]);
    
    // Show instant notification
    const showNotification = useCallback((title, body, options = {}) => {
        if (permission !== 'granted') {
            console.log('Notification permission not granted');
            return;
        }
        
        if (swRegistration && swRegistration.active) {
            swRegistration.showNotification(title, {
                body,
                icon: '/logo192.png',
                badge: '/logo192.png',
                vibrate: [100, 50, 100],
                ...options
            });
        } else if ('Notification' in window) {
            new Notification(title, {
                body,
                icon: '/logo192.png',
                ...options
            });
        }
    }, [permission, swRegistration]);
    
    const value = {
        permission,
        requestPermission,
        scheduleNotification,
        cancelNotification,
        scheduleEventReminders,
        showNotification,
        isSupported: 'Notification' in window
    };
    
    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider;
